import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { recordRegistrationPageView } from '../../services/registrationPageViewService';
import { readPendingReferralCode } from '../../services/referralService';

const REGISTRATION_PATHS = new Set([
  '/register/provider',
  '/register/company',
  // /join renders the company registration flow at its claim-code door.
  '/join',
]);

// A colleague's invite link. The code is the whole point of the visit, so it
// travels as its own field and the recorded url stays the bare '/r'.
const INVITE_LANDING = /^\/r\/([A-Za-z0-9-]{3,40})$/;

/**
 * The invite code behind a provider signup, from the link itself (?ref=, which
 * ProviderRegistration strips with replaceState after the router has already
 * read it), the pre-React bootstrap stash, or the code ReferralLanding stored.
 * Peeks only: ProviderRegistration still owns consuming the bootstrap copy.
 */
function inviteCodeFor(search) {
  try {
    const fromUrl = new URLSearchParams(search).get('ref');
    const fromBootstrap =
      typeof window !== 'undefined' ? window.__RXR_REFERRAL_CODE__ : null;
    const code = fromUrl || fromBootstrap || readPendingReferralCode();
    return code ? String(code).trim().toUpperCase() : null;
  } catch {
    return null;
  }
}

function normalizedPath(pathname) {
  if (!pathname || pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '');
}

export default function RegistrationPageViewTracker() {
  const location = useLocation();
  const lastRecordedPath = useRef(null);

  useEffect(() => {
    const pathname = normalizedPath(location.pathname);
    const invite = INVITE_LANDING.exec(pathname || '');
    if (invite) {
      if (lastRecordedPath.current === pathname) return;
      lastRecordedPath.current = pathname;
      recordRegistrationPageView('/r', {
        referralCode: invite[1].toUpperCase(),
      });
      return;
    }
    if (!REGISTRATION_PATHS.has(pathname)) {
      lastRecordedPath.current = null;
      return;
    }

    // React 18 StrictMode replays mount effects in development. The ref is
    // preserved across that replay, preventing a duplicate visitor-map row.
    if (lastRecordedPath.current === pathname) return;
    lastRecordedPath.current = pathname;
    const referralCode =
      pathname === '/register/provider' ? inviteCodeFor(location.search) : null;
    if (referralCode) {
      recordRegistrationPageView(pathname, { referralCode });
    } else {
      recordRegistrationPageView(pathname);
    }
    // location.search is read at entry only; a later query change on the same
    // page is not a new visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
